// ─────────────────────────────────────────────────────────────────────────────
// WHAT THIS SCREEN IS, FOR SOMEBODY WHO DID NOT BUILD IT.
//
// The chart check answers three questions on every chart that had activity, and almost
// everything about how it decides WHICH charts is invisible: the window is four signals
// rather than a timestamp, the data is a nightly mirror rather than a live read, and two of
// the nine questions cannot be answered at all until PointClickCare grants a scope. A unit
// manager looking at a short list has no way to tell "quiet night" from "the scan did not
// run", and no way to tell "we checked the labs" from "we are not allowed to".
//
// Every one of those is already stated SOMEWHERE — in a leg's `because`, in a scope note,
// in a headline that only appears in one state. This gathers them, so the answer to "why is
// this chart here" does not require opening three rows and inferring.
//
// ── COLLAPSED BY DEFAULT, AND THAT IS THE POINT ─────────────────────────────
//
// The named risk on this screen is overwhelm — she warned twice that it leads to "non-usage
// or under-usage". An explainer that is always open is nine paragraphs between a manager
// and her worklist every single morning. It is read once, in the first week, and after that
// it is a thing you go back to when a number looks wrong.
//
// It also deliberately does NOT repeat the four marks. The legend above the table does
// that, it is always visible, and a second copy that drifts from the first is worse than no
// copy — the facility-level banner already proved that by keeping violet for a fortnight
// after the state turned amber.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";

const SECTIONS = [
  {
    q: "What is the three-point chart check?",
    a: [
      "Three questions on one chart: the medications and orders, the progress note, and the labs. " +
        "It is a habit rather than an audit — the point is to do it every morning on the charts that " +
        "changed, not to do it thoroughly once a quarter.",
      "If a chart needs a real audit, that is the post-incident worksheet, and it is a different screen " +
        "with a different standard of proof. This one is triage.",
    ],
  },
  {
    q: "How does a chart get onto this list?",
    a: [
      "Something happened on it in the window. Not a judgement about risk, and not a ranking — the list " +
        "is every chart with activity, in the order the scan saw them.",
      "Activity means one of four things, and the fourth is the one most systems miss: an order was " +
        "entered, an order first appeared to us, an order was backdated into the window, or an order " +
        "was STOPPED. A discontinued order has an old entry date and an old start date, so without that " +
        "fourth signal a D/C never reaches the list at all.",
    ],
  },
  {
    q: "How fresh is it?",
    a: [
      "It reads a nightly mirror, not PointClickCare directly. The scan runs at 08:00 UTC and copies the " +
        "medications, orders and progress notes it is allowed to see; this screen reads that copy.",
      "So a change made after the scan is not here yet. When the newest order we hold is older than the " +
        "window, the headline says so in its own words rather than showing you a short list that looks " +
        "like a quiet night.",
    ],
  },
  {
    q: "Why does the labs column never get checked?",
    a: [
      "Because we are not allowed to read labs. The scope is not granted to this integration and no date " +
        "has been given for it — unlike care plans and the rest of the order book, which open in " +
        "November 2026.",
      "It is marked as not checked rather than left blank or shown as clear, because a tick there would " +
        "be this product telling you your labs are reviewed when nothing has looked at them. Both halves " +
        "are blocked: a lab ORDER is activity on the chart the day it is written, and a lab RESULT is a " +
        "different fact. Neither is readable.",
    ],
  },
  {
    q: "What does ticking a chart do?",
    a: [
      "It records that a person looked, with who and when. Nothing else.",
      "It is not a compliance answer, it does not enter any percentage, and no row on any worksheet " +
        "changes because of it. A ticked chart stays on the list and reads as done — removing it would " +
        "take away the only way to check your own work.",
    ],
  },
  {
    // ADDED WITH THE PICKER. The first question anybody asks after answering is whether the
    // column should now be green, and the honest answer is no — so it is answered here as
    // well as beside the answer itself.
    q: "I ticked a chart and it asked what happened. What is that for?",
    a: [
      "An order written with no progress note beside it is reported in the nightly email for up to a " +
        "week, not just for the day it appears on this screen. That is deliberate: a Friday-night order " +
        "used to drop off the list by Saturday morning whether or not anybody had seen it.",
      "The three answers — the note was added, no note was needed, or it was referred — stop that email " +
        "repeating a chart somebody has already judged. Only an answer does that; a bare tick does not, " +
        "because 'somebody looked' is not 'somebody decided nothing was needed'.",
      "It changes no mark. The columns read the orders and the notes in the chart, so the medications " +
        "column stays as it is until a note actually appears. 'No note needed' asks for a reason because " +
        "it is the only one of the three that nothing in the record can confirm for you.",
    ],
  },
  {
    q: "What counts as something to look at?",
    a: [
      "A check that could be made and found something. The medications and orders leg reads the sig for " +
        "the things a surveyor turns into a tag: a missing route, a hold parameter that a drug class " +
        "needs and the order does not state, two analgesics covering the same pain score.",
      "A progress note is never judged for you. It is marked as there to read, because a note is prose " +
        "and the reading is the check.",
    ],
  },
];

export default function ChartCheckExplainer({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section style={{ marginBottom: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 11px",
          borderRadius: "var(--ed-r-sm)",
          border: "1px solid var(--ed-line-hover)",
          background: open ? "var(--ed-brand-soft)" : "var(--ed-canvas)",
          color: open ? "var(--ed-brand)" : "var(--ed-ink-2)",
          font: "inherit",
          fontSize: 12.5,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden="true"
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}
        >
          <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        How this screen works
      </button>

      {open ? (
        <div
          style={{
            marginTop: 9,
            border: "1px solid var(--ed-line)",
            borderRadius: "var(--ed-r-lg)",
            background: "var(--ed-line-2)",
            padding: "4px 16px 14px",
          }}
        >
          {SECTIONS.map((s) => (
            <div key={s.q} style={{ marginTop: 14 }}>
              <h3 style={{ margin: "0 0 5px", fontSize: 13.5, fontWeight: 800, color: "var(--ed-ink)", lineHeight: 1.3 }}>
                {s.q}
              </h3>
              {s.a.map((p, i) => (
                <p key={i} style={{ margin: "0 0 6px", fontSize: 13, lineHeight: 1.55, color: "var(--ed-ink-2)", maxWidth: "74ch" }}>
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export { SECTIONS };
